<template>
  <div :data-widget-title="content.optionalTitle?.text">
    <div
      v-if="entries.length"
      class="w-full px-box-3"
      :class="{
        'max-w-screen-md mx-auto box-container pb-box-2 my-4': content.layout === 'free_100',
      }"
    >
      <SliceTitle
        class="text-center lg:text-center pb-2 pt-16 md:pt-20"
        :class="content.layout === 'boxed_66' ? 'subtitle1' : 'title lg:pb-6'"
        :value="content.optionalTitle"
      />
      <ul>
        <li v-for="(item, index) in entries" :key="index">
          <details
            class="expandable bg-white border-gray-200 transition-max-height duration-700 ease-in px-box-1 text-16 sm:text-20 py-8"
            :class="{
              'border-t': index !== 0,
            }"
            :open="isOpen(index)"
          >
            <summary
              class="flex items-center justify-between font-bold cursor-pointer list-none relative pr-8"
            >
              {{ item.question }}
            </summary>
            <PortableText :content="item.answer" class="typography" />
          </details>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { FAQPage, WithContext } from 'schema-dts'
import { toPlainText } from '@portabletext/vue'
import type { GQLFaqSlice } from './generated/faq'

const props = defineProps<{ content: GQLFaqSlice }>()

const entries = computed(() => {
  return props.content.entries.filter(
    ({ question, answer }) =>
      !hasInvalidPlaceholderValue(question) && !hasInvalidPlaceholderValue(answer),
  )
})

if (props.content.markupEnabled && entries.value.length) {
  const faqMarkup: WithContext<FAQPage> = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.value.map((entry) => {
      return {
        '@type': 'Question',
        name: entry.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: toPlainText(entry.answer),
        },
      }
    }),
  }
  useHead({
    script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(faqMarkup) }],
  })
}

function isOpen(index: number) {
  if (props.content.display === 'FirstExpanded') {
    return index === 0
  }
  if (props.content.display === 'FirstThreeExpanded') {
    return index < 3
  }
  return props.content.display === 'Expanded'
}
</script>

<style scoped>
.expandable[open] {
  @apply max-h-9999;
}

.expandable summary::after {
  @apply flex justify-center items-center shrink-0 text-16 lg:text-20 bg-contain absolute top-0 right-0;
  content: '+';
}

.expandable summary:hover::after {
  @apply text-gray-700;
}

.expandable[open] > summary::after {
  content: '-';
}

summary::-webkit-details-marker {
  @apply hidden;
}

summary:focus:not(:focus-visible) {
  @apply outline-none;
}
</style>
